import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Info = 'info',
  Skipped = 'Skipped',
}

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
  /** Override the default self-positioning (used by ToastQueueProvider to stack
   * multiple toasts inside its own fixed container instead of each toast
   * independently claiming the same fixed screen position). */
  positionClassName?: string;
  /** ToastQueueProvider already renders its own fixed, viewport-anchored
   * stacking container as a sibling at the top of the layout tree, so
   * portaling its toasts too would pull them out of that container and
   * break the stacked layout - it opts out via this flag. Every other
   * (non-queue) call site keeps the default portal so a `transform`-bearing
   * ancestor (e.g. a page's fade-in wrapper) can't hijack its fixed
   * positioning away from the viewport. */
  disablePortal?: boolean;
}

export function CustomToast({
  message,
  type = ToastType.Success,
  onClose,
  duration = 3000,
  positionClassName = 'fixed bottom-4 right-20 z-[1000]',
  disablePortal = false,
}: ToastProps) {
  const { t } = useTranslation(['common']);
  const [isClosing, setIsClosing] = useState(false);

  const getTranslatedType = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return t('success');
      case ToastType.Error:
        return t('error');
      case ToastType.Info:
        return t('info');
      default:
        return t('info');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(onClose, 300); // Wait for the fade-out animation to complete (300ms)
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return <CheckCircleIcon className="h-8 w-8 text-successDefault" />;
      case ToastType.Error:
        return <ExclamationTriangleIcon className="h-8 w-8 text-btnColor" />;
      case ToastType.Info:
        return <InformationCircleIcon className="h-8 w-8 text-secondarySemiDark" />;
      default:
        return <InformationCircleIcon className="h-8 w-8 text-secondarySemiDark" />;
    }
  };

  // Determine text color based on type
  const getTextColorClass = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return 'text-successDefault';
      case ToastType.Error:
        return 'text-btnColor';
      case ToastType.Info:
        return 'text-secondarySemiDark';
      default:
        return 'text-warningDefault';
    }
  };

  // Determine border color based on type
  const getBorderColorClass = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return 'border-successDefault';
      case ToastType.Error:
        return 'border-btnColor';
      case ToastType.Info:
        return 'border-secondarySemiDark';
      default:
        return 'border-warningDefault';
    }
  };

  const toast = (
    <div
      className={`${positionClassName} min-w-60 max-w-96 overflow-hidden rounded-2xl border border-l-4 bg-white ${getBorderColorClass(
        type
      )} ${isClosing ? 'animate-toast-fade-out-up' : 'animate-toast-slide-in'}`}
    >
      <div className={`shadow-[ 0px 4px 8px 0px #0000001A] rounded-lg bg-white p-3 shadow`}>
        <div className="flex items-center space-x-2">
          {/* Icon */}
          <div className="flex items-center">{getIcon(type)}</div>
          <div>
            <h5 className={`!font-medium capitalize ${getTextColorClass(type)}`}>
              {getTranslatedType(type)}!
            </h5>
            <p className={`bodyXs flex-grow text-neutralDark`}>{message}</p>
          </div>
          {/* Close Button */}
          <button
            onClick={() => {
              setIsClosing(true);
              setTimeout(onClose, 300);
            }}
            className={`rounded-full p-1 ${getTextColorClass(type)} hover:bg-neutralLightmedium hover:bg-opacity-10`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );

  // Portal to document.body by default so a transform-bearing ancestor
  // anywhere up the tree (e.g. a page's animate-fade-in wrapper, which
  // keeps a non-none `transform` after its fill-mode: both animation ends)
  // can't become this toast's containing block and break its `fixed`
  // positioning relative to the viewport. Guarded for SSR, where
  // document is unavailable.
  if (disablePortal || typeof document === 'undefined') {
    return toast;
  }
  return createPortal(toast, document.body);
}
